import { Switch } from 'antd';
import { observer } from 'mobx-react';
import { useCallback } from 'react';
import { Section } from '../../../../components/form-fields/section';
import { BACKGROUND_COLORS } from '~components/background-color-picker/constants';
import {
    useApplicationSelectionStore,
    useBusinessUnitStore,
} from '~contexts/mobx';
import {
    ApplicationTargetEnum,
    ClientProvinceEnum,
} from '~types/app-selection';
import { WeatherActivationSlider } from '../WeatherActivationSlider';
import style from './style.module.scss';

interface StatusSectionProps {
    values: {
        isActive?: boolean;
        hasPreview?: boolean;
        isVisibleOnHomePage?: boolean;
        isPromotedBanner?: boolean;
        isForMajor?: boolean;
        isForCarwash?: boolean;
        isKillerPromo?: boolean;
        isForRoadToRewards?: boolean;
        isForFuelTester?: boolean;
        isForLabatt?: boolean;
        autoReissueEnabled?: boolean;
        provinces?: { provinceId: number }[];
        isForCTConnect?: boolean | null;
        type?: string | null;
        temperatureActivationFloor?: number | null;
        temperatureActivationCeiling?: number | null;
        items?: {
            backgroundColor?: string | null;
            promotedBannerTextColor?: string | null;
        }[];
    };
    setFieldValue: (field: string, value: unknown) => void;
    isLoading?: boolean;
}

interface StatusToggleProps {
    label: string;
    checked: boolean;
    onChange: (checked: boolean) => void;
    loading?: boolean;
    disabled?: boolean;
}

function StatusToggle(props: StatusToggleProps) {
    return (
        <div className={style.label_and_toggle}>
            <label className="footnote_description_bold">{props.label}</label>
            <Switch
                checked={props.checked}
                onChange={props.onChange}
                loading={props.loading}
                disabled={props.disabled}
            />
        </div>
    );
}

export const StatusSection = observer(function StatusSection({
    values,
    setFieldValue,
    isLoading = false,
}: StatusSectionProps) {
    const applicationSelectionStore = useApplicationSelectionStore();
    const businessUnitStore = useBusinessUnitStore();
    const isQcTarget =
        applicationSelectionStore.appTarget === ApplicationTargetEnum.QC;

    const isOnlyQuebecSelected =
        values.provinces?.length === 1 &&
        values.provinces[0].provinceId ===
            businessUnitStore.getProvinceIdByCode(ClientProvinceEnum.QC);

    const createToggleHandler = (field: string) => (checked: boolean) =>
        setFieldValue(field, checked);

    const isWeatherPromotionActive =
        values.temperatureActivationFloor != null ||
        values.temperatureActivationCeiling != null;

    const handleWeatherToggleChange = useCallback(
        (checked: boolean) => {
            if (!checked) {
                setFieldValue('temperatureActivationFloor', null);
                setFieldValue('temperatureActivationCeiling', null);
            } else {
                if (values.temperatureActivationFloor == null) {
                    setFieldValue('temperatureActivationFloor', -50);
                }
                if (values.temperatureActivationCeiling == null) {
                    setFieldValue('temperatureActivationCeiling', 50);
                }
            }
        },
        [
            setFieldValue,
            values.temperatureActivationFloor,
            values.temperatureActivationCeiling,
        ]
    );

    const clearPromotedBannerColors = useCallback(() => {
        if (values.items) {
            values.items.forEach((_, index) => {
                setFieldValue(`items[${index}].promotedBannerTextColor`, null);
            });
        }
    }, [setFieldValue, values.items]);

    const handlePromotedBannerToggle = useCallback(
        (checked: boolean) => {
            setFieldValue('isPromotedBanner', checked);
            if (checked && values.items) {
                values.items.forEach((item, index) => {
                    if (item.backgroundColor) {
                        const colorOption = BACKGROUND_COLORS.find(
                            (c) => c.value === item.backgroundColor
                        );
                        if (!colorOption) {
                            console.warn(
                                `No matching color found for backgroundColor: ${item.backgroundColor}`
                            );
                        }
                        setFieldValue(
                            `items[${index}].promotedBannerTextColor`,
                            colorOption?.titleColor ?? null
                        );
                    }
                });
            } else if (!checked) {
                clearPromotedBannerColors();
            }
        },
        [setFieldValue, values.items, clearPromotedBannerColors]
    );

    return (
        <Section title="Status">
            <div className={style.status_column}>
                <StatusToggle
                    label="Active"
                    checked={values.isActive ?? false}
                    onChange={createToggleHandler('isActive')}
                />
                <StatusToggle
                    label="Show on Homepage"
                    checked={values.isVisibleOnHomePage ?? false}
                    onChange={(checked) => {
                        setFieldValue('isVisibleOnHomePage', checked);
                        if (!checked) {
                            setFieldValue('isPromotedBanner', false);
                            clearPromotedBannerColors();
                        }
                    }}
                />
                {isQcTarget && isOnlyQuebecSelected && (
                    <StatusToggle
                        label="Age above 18 only"
                        checked={values.isForMajor ?? false}
                        onChange={createToggleHandler('isForMajor')}
                    />
                )}
                <StatusToggle
                    label="Carwash Only"
                    checked={values.isForCarwash ?? false}
                    onChange={createToggleHandler('isForCarwash')}
                />
                <StatusToggle
                    label="Labatt Only"
                    checked={values.isForLabatt ?? false}
                    onChange={(checked) => {
                        setFieldValue('isForLabatt', checked);
                        if (checked) setFieldValue('isPrivate', true);
                    }}
                />
                <StatusToggle
                    label="Killer Promo Only"
                    checked={values.isKillerPromo ?? false}
                    onChange={createToggleHandler('isKillerPromo')}
                    disabled={values.type === 'in_store'}
                />
                <StatusToggle
                    label="Road to Rewards campaign"
                    checked={values.isForRoadToRewards ?? false}
                    onChange={createToggleHandler('isForRoadToRewards')}
                    disabled={values.type === 'in_store'}
                />
                <StatusToggle
                    label="Fuel tester only"
                    checked={values.isForFuelTester ?? false}
                    onChange={createToggleHandler('isForFuelTester')}
                    disabled={values.type === 'in_store'}
                />
                <StatusToggle
                    label="Automatically reissue"
                    checked={values.autoReissueEnabled ?? false}
                    onChange={createToggleHandler('autoReissueEnabled')}
                    loading={isLoading}
                />
                <StatusToggle
                    label="Preview Image"
                    checked={values.hasPreview ?? false}
                    onChange={createToggleHandler('hasPreview')}
                />
                {values.isVisibleOnHomePage && values.type === 'in_store' && (
                    <StatusToggle
                        label="Show as meal deal"
                        checked={values.isPromotedBanner ?? false}
                        onChange={handlePromotedBannerToggle}
                    />
                )}
                {values.type === 'coupon' && values.isForCTConnect && (
                    <StatusToggle
                        label="CT Connect"
                        checked={values.isForCTConnect ?? false}
                        onChange={createToggleHandler('isForCTConnect')}
                    />
                )}
                <StatusToggle
                    label="Weather-dependent"
                    checked={isWeatherPromotionActive}
                    onChange={handleWeatherToggleChange}
                />

                {/* Weather activation section (shown when weather toggle is active) */}
                {isWeatherPromotionActive && (
                    <div className={style.slider_container}>
                        <WeatherActivationSlider
                            temperatureActivationFloor={
                                values.temperatureActivationFloor
                            }
                            temperatureActivationCeiling={
                                values.temperatureActivationCeiling
                            }
                            setFieldValue={setFieldValue}
                        />
                    </div>
                )}
            </div>
        </Section>
    );
});
